import {
  Box,
  Text,
  Input,
  Grid,
  VStack,
  HStack,
  Button,
  Checkbox,
  ColorPicker,
  parseColor,
  Slider,
} from "@chakra-ui/react";
import { useModelStore, usePlacedModelsStore, useUserInterfaceStore } from "../../store";
import { deleteAsset } from "../../services/assetsApi";
import MusicUploadForObject from "./MusicUploadForObject";

import { useState, useEffect } from "react";
function hsbToHsl(h, s, b) {
  s /= 100;
  b /= 100;

  const l = b * (1 - s / 2);
  const sl = l === 0 || l === 1 ? 0 : (b - l) / Math.min(l, 1 - l);

  return {
    h: h / 360, // Three.js expects 0–1
    s: sl,
    l: l,
  };
}

function TransformInputs({ label, value, onChange }) {
  const [local, setLocal] = useState(value);

  // Sync local state when parent value changes
  useEffect(() => {
    setLocal(value);
  }, [value]);

  const handleChange = (index, newVal) => {
    const updated = [...local];
    updated[index] = newVal;
    setLocal(updated);
    onChange(updated); // propagate up
  };

  return (
    <Box>
      <Text fontSize="xs" mb={1} color="gray.400">
        {label}
      </Text>
      <Grid templateColumns="repeat(3, 1fr)" gap={2}>
        {local.map((val, i) => (
          <Input
            key={i}
            size="xs"
            type="number"
            value={val}
            step={1}
            onChange={(e) => {
              const parsed = parseFloat(e.target.value) || 0;
              handleChange(i, parsed);
            }}
            bg="gray.800"
            border="1px solid"
            borderColor="gray.600"
            _focus={{ borderColor: "blue.400" }}
          />
        ))}
      </Grid>
    </Box>
  );
}

function SceneObjectDetails() {
  const roomId = usePlacedModelsStore((s) => s._roomId);
  const selected = useModelStore((s) => s.selectedMeshes);
  const updateTransform = usePlacedModelsStore((s) => s.updateTransform);
  const removeModel = usePlacedModelsStore((s) => s.removeModel);
  const updateName = usePlacedModelsStore((s) => s.updateName);
  const updateMusic = usePlacedModelsStore((s) => s.updateMusic);
  const updateMusicLoop = usePlacedModelsStore((s) => s.updateMusicLoop);
  const updateLink = usePlacedModelsStore((s) => s.updateLink);
  const updateHoverText = usePlacedModelsStore((s) => s.updateHoverText);
  const updateHue = usePlacedModelsStore((s) => s.updateHue);
  const updateRoughness = usePlacedModelsStore((s) => s.updateRoughness);
  const cameraControls = useModelStore((s) => s.cameraControls);
  const setSelectedMeshes = useModelStore((s) => s.setSelectedMeshes);
  const setRotationAxes = useUserInterfaceStore((s) => s.setRotationAxes);

  const obj = selected?.[0];
  const model = usePlacedModelsStore((s) =>
    s.placedModels.find((m) => m.id === obj?.userData?.sourceId)
  );

  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState("");
  const [showTransforms, setShowTransforms] = useState(false);
  const [interactionMode, setInteractionMode] = useState("none"); // "none" | "link" | "music"

  const {
    id,
    name,
    position,
    rotation,
    scale,
    link,
    music,
    musicAssetKey,
    musicLoop = true,
  } = model || {};

  const handleRename = () => {
    if (newName.trim()) {
      updateName(id, newName.trim());
    }
    setIsEditing(false);
  };

  const handleSaveLink = () => {
    if (_link) {
      updateLink(id, _link);
    }
    updateHoverText(id, _hoverText);
    setHasUnsavedLinkChanges(false);
  };

  const [color, setColor] = useState(parseColor("red"));
  const [_roughness, setRoughness] = useState([1]);
  const [_link, setLink] = useState(link);
  const [_hoverText, setHoverText] = useState("");
  const [hasUnsavedLinkChanges, setHasUnsavedLinkChanges] = useState(false);

  const handleMusicChange = ({ url, assetKey }) => {
    updateMusic(id, url, assetKey);
  };

  const handleMusicRemove = () => {
    if (musicAssetKey) {
      deleteAsset(musicAssetKey, { roomId }).catch(console.error);
    }
    updateMusic(id, null, null);
  };

  useEffect(() => {
    if (!model) return;
    const { link = "", hoverText = "", music = null, roughness = 1 } = model;
    setLink(link);
    setHoverText(hoverText);
    setRoughness([roughness]);
    setHasUnsavedLinkChanges(false);

    // Sync interaction mode from model data
    if (music) {
      setInteractionMode("music");
    } else if (link) {
      setInteractionMode("link");
    } else {
      setInteractionMode("none");
    }
  }, [model, id]);

  // Sync color picker and reset rotation axis when selected object changes
  useEffect(() => {
    if (!id || !model) return;
    const hueInDegrees = Math.round((model.hue ?? 0) * 360);
    setColor(parseColor(`hsl(${hueInDegrees}, 100%, 50%)`));
    setRotationAxes({ x: false, y: true, z: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // intentionally only [id] — avoids resetting during active drag

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Delete" && model) {
        removeModel(model?.id);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [model, model?.id, removeModel]);

  if (!model) {
    return (
      <Text fontSize="sm" color="red.500">
        Model not found
      </Text>
    );
  }

  return (
    <VStack align="start" spacing={4} fontSize="sm">
      <Box>
        <Text fontSize="xs" color="gray.500" wordBreak="break-word">
          UUID: {id}
        </Text>
        {isEditing ? (
          <Input
            size="sm"
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onBlur={handleRename}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") handleRename();
              if (e.key === "Escape") setIsEditing(false);
            }}
            bg="gray.800"
            borderColor="gray.600"
          />
        ) : (
          <Text
            fontWeight="bold"
            color="white"
            cursor="pointer"
            onDoubleClick={() => {
              setNewName(name || "");
              setIsEditing(true);
            }}
          >
            {name || "Unnamed Mesh"}
          </Text>
        )}
      </Box>

      <Box w={{ base: "80%", md: "100%" }} mx={{ base: "auto", md: "0" }}>
        <ColorPicker.Root
          open
          value={color}
          onValueChange={(c) => {
            setColor(c.value);
            const hsb = c.value;
            const hsl = hsbToHsl(hsb.hue, hsb.saturation, hsb.brightness);
            const newHue = hsl.h ?? 0;
            updateHue(id, newHue, false);
          }}
          onValueChangeEnd={(c) => {
            setColor(c.value);
            const hsb = c.value;
            const hsl = hsbToHsl(hsb.hue, hsb.saturation, hsb.brightness);
            const newHue = hsl.h ?? 0;
            updateHue(id, newHue, true);
          }}
        >
          <ColorPicker.HiddenInput />
          <ColorPicker.Content animation="none" shadow="none" padding="0">
            <HStack>
              <ColorPicker.ChannelSlider channel="hue" />
            </HStack>
          </ColorPicker.Content>
        </ColorPicker.Root>
      </Box>


      <Slider.Root
        defaultValue={[1]}
        width={"100%"}
        size="md"
        value={_roughness}
        min={0}
        max={1}
        step={0.01}
        onValueChange={(e) => {
          setRoughness(e.value);
          updateRoughness(id, e.value[0], false);
        }}
        onValueChangeEnd={(e) => updateRoughness(id, e.value[0], true)}
      >
        <Slider.Label>Roughness</Slider.Label>
        <Slider.Control>
          <Slider.Track bg="gray.600">
            <Slider.Range bg="gray.300" />
          </Slider.Track>
          <Slider.Thumbs />
        </Slider.Control>
      </Slider.Root>

      {/* Collapsible Transform Section */}
      <Box w="100%">
        <HStack
          cursor="pointer"
          onClick={() => setShowTransforms(!showTransforms)}
          spacing={2}
          mb={showTransforms ? 3 : 0}
        >
          <Text fontSize="xs" color="gray.400" fontWeight="medium">
            {showTransforms ? "▼" : "▶"} Transform
          </Text>
        </HStack>
        {showTransforms && (
          <VStack spacing={3} align="stretch">
            <TransformInputs
              label="Position"
              value={position}
              onChange={(v) => updateTransform(id, { position: v })}
            />
            <TransformInputs
              label="Rotation"
              value={rotation}
              onChange={(v) => updateTransform(id, { rotation: v })}
            />
            <TransformInputs
              label="Scale"
              value={scale}
              onChange={(v) => updateTransform(id, { scale: v })}
            />
          </VStack>
        )}
      </Box>

      {/* Link Section */}
      <Box
        w="100%"
        border="1px solid"
        borderColor={interactionMode === "music" ? "gray.700" : "gray.600"}
        bg="gray.800"
        p={3}
        opacity={interactionMode === "music" ? 0.4 : 1}
        pointerEvents={interactionMode === "music" ? "none" : "auto"}
        transition="opacity 0.2s"
      >
        <Text fontSize="sm" mb={3} color="gray.300" fontWeight="medium">
          Add Link
        </Text>
        <VStack spacing={3} align="stretch">
          <Box>
            <Text fontSize="xs" mb={1} color="gray.400">
              URL
            </Text>
            <Input
              size="sm"
              value={_link || ""}
              onChange={(e) => {
                setLink(e.target.value);
                setHasUnsavedLinkChanges(true);
              }}
              placeholder="Enter URL..."
              bg="gray.900"
              borderColor="gray.600"
              _focus={{ borderColor: "blue.400" }}
            />
          </Box>
          <Box>
            <Text fontSize="xs" mb={1} color="gray.400">
              Hover Text
            </Text>
            <Input
              size="sm"
              value={_hoverText || ""}
              onChange={(e) => {
                setHoverText(e.target.value);
                setHasUnsavedLinkChanges(true);
              }}
              placeholder="Enter Hover Text..."
              bg="gray.900"
              borderColor="gray.600"
              _focus={{ borderColor: "blue.400" }}
            />
          </Box>
          <Button
            size="sm"
            width="100%"
            onClick={handleSaveLink}
            bg={hasUnsavedLinkChanges ? "#26EF96" : "gray.600"}
            color={hasUnsavedLinkChanges ? "black" : "white"}
            _hover={{
              bg: hasUnsavedLinkChanges ? "#1fd47f" : "gray.500",
            }}
            fontWeight="medium"
          >
            Save Link
          </Button>
        </VStack>
      </Box>

      {/* Music Section */}
      <Box
        w="100%"
        border="1px solid"
        borderColor={interactionMode === "link" ? "gray.700" : "gray.600"}
        bg="gray.800"
        p={3}
        opacity={interactionMode === "link" ? 0.4 : 1}
        pointerEvents={interactionMode === "link" ? "none" : "auto"}
        transition="opacity 0.2s"
      >
        <Text fontSize="sm" mb={3} color="gray.300" fontWeight="medium">
          Add Music
        </Text>
        <MusicUploadForObject
          modelId={id}
          currentMusicUrl={music}
          onMusicChange={handleMusicChange}
          onMusicRemove={handleMusicRemove}
        />
        {music && (
          <Checkbox.Root
            variant="solid"
            checked={musicLoop}
            onCheckedChange={(e) => updateMusicLoop(id, !!e.checked)}
            mt={3}
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
            <Checkbox.Label>
              <Text fontSize="sm" color="gray.300">
                Loop
              </Text>
            </Checkbox.Label>
          </Checkbox.Root>
        )}
      </Box>

      <HStack spacing={3}>
        <Button
          size="sm"
          colorPalette="white"
          color="white"
          variant="outline"
          onClick={() => {
            if (cameraControls) {
              cameraControls.fitToSphere(obj, true);
            }
          }}
        >
          Zoom To Model
        </Button>

        <Button
          size="sm"
          colorPalette="red"
          variant="solid"
          onClick={() => {
            setSelectedMeshes([]);

            removeModel(id);
          }}
        >
          Remove Model
        </Button>
      </HStack>
    </VStack>
  );
}

export default SceneObjectDetails;
